/**
 * Highcharts initialization and management
 */

import { setChart } from './state.js';
import { getMinutesElapsedToday } from '../utils/time.js'; // Used by createPrecipDurationChart
import * as ChartFactory from './ChartFactory.js';

// Chart configuration options
const timeseriesOptions = {
  accessibility: {
    enabled: false,
  },
};

/**
 * Initialize all charts
 * @param {object} chartData - Data from template (cWind, cPrecip, etc.)
 */
export function initializeCharts(chartData) {
  if (typeof Highcharts === 'undefined') {
    console.error('[Charts] Highcharts is not loaded');
    return;
  }

  // Initialize each chart and store in state
  // Note: Wind gauge removed - replaced by wind rose + bar display
  // Note: Precip gauge removed - replaced by precip bar display
  initializePrecipBar(chartData.cPrecip);
  setChart('precipDuration', createPrecipDurationChart(chartData.cPM));
  setChart('uv', createUVChart(chartData.cUV));
  setChart('dp', createTemperatureChart(chartData.cT, chartData.cDP));
  setChart('timeSeries', createTimeSeriesChart());
  setChart('timeSeriesSensors', createTimeSeriesSensorsChart());
}

/**
 * Initialize precipitation bar display
 * @param {number} precipIn - Initial precipitation in inches
 */
function initializePrecipBar(precipIn) {
  updatePrecipBar(precipIn);
}

/**
 * Update precipitation bar display
 * @param {number} precipIn - Precipitation amount in inches
 */
export function updatePrecipBar(precipIn) {
  const maxPrecip = 1; // Max precip for bar scale (1 inch)
  const amountEl = document.getElementById('precip-bar-amount');
  const fillEl = document.getElementById('precip-bar-fill');

  if (amountEl) {
    amountEl.textContent = precipIn.toFixed(2);
  }

  if (fillEl) {
    const heightPct = Math.min((precipIn / maxPrecip) * 100, 100);
    fillEl.style.height = `${heightPct}%`;

    // Color gradient based on amount
    if (precipIn < 0.1) {
      fillEl.style.background = 'linear-gradient(to top, #a8e6cf, #dcedc1)';
    } else if (precipIn < 0.25) {
      fillEl.style.background = 'linear-gradient(to top, #88d8b0, #a8e6cf)';
    } else if (precipIn < 0.5) {
      fillEl.style.background = 'linear-gradient(to top, #00bf3e, #7dd98a)';
    } else {
      fillEl.style.background = 'linear-gradient(to top, #007a27, #00bf3e)';
    }
  }
}

/**
 * Create precipitation duration gauge chart
 */
function createPrecipDurationChart(cPM) {
  return ChartFactory.createFullCircleGauge(
    'chart-precip-duration',
    '',
    cPM,
    0,
    getMinutesElapsedToday(),
    'min',
    0,
  );
}

/**
 * Create UV index gauge chart
 */
function createUVChart(cUV) {
  return ChartFactory.createBandedGauge(
    'chart-uv',
    '',
    cUV,
    0,
    12,
    [
      { from: 0, to: 3, color: 'rgba(62,167,45,1)' },
      { from: 3, to: 6, color: 'rgba(255,243,0,1)' },
      { from: 6, to: 8, color: 'rgba(241,139,0,1)' },
      { from: 8, to: 11, color: 'rgba(229,50,16,1)' },
      { from: 11, to: 20, color: 'rgba(181,103,164,1)' },
    ],
    '', // no unit
    0,
  );
}

/**
 * Create temperature and dew point dual gauge chart
 */
function createTemperatureChart(cT, cDP) {
  return ChartFactory.createDualGauge(
    'chart-dp',
    '',
    cT,
    cDP,
    [
      [0.0, '#1ffbff'],
      [0.32, '#637dff'],
      [0.70, '#c6ff36'],
      [1.0, '#fc0303'],
    ],
    [
      [0.0, '#5c4e4b'],
      [0.32, '#e6e229'],
      [0.70, '#00c431'],
      [1.0, '#00ffc8'],
    ],
  );
}

/**
 * Create weather time series chart
 */
function createTimeSeriesChart() {
  return Highcharts.chart(
    'chart-timeseries',
    Highcharts.merge(timeseriesOptions, {
      chart: {},
      title: {
        text: null,
      },
      xAxis: {
        type: 'datetime',
        tickInterval: 2 * 3600 * 1000,
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%l%P',
          day: '%e %b',
          week: '%e %b',
          month: '%b \'%y',
          year: '%Y',
        },
        labels: {
          rotation: 45,
        },
        gridLineWidth: 1,
        minorTicks: true,
      },
      yAxis: {
        minorTicks: true,
      },
    }),
  );
}

/**
 * Create sensors time series chart
 */
function createTimeSeriesSensorsChart() {
  return Highcharts.chart(
    'chart-timeseries-sensors',
    Highcharts.merge(timeseriesOptions, {
      // Taller so the two stacked panes each have room to read.
      chart: {
        height: 520,
      },
      title: {
        text: null,
      },
      xAxis: {
        type: 'datetime',
        tickInterval: 2 * 3600 * 1000,
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S.%L',
          second: '%H:%M:%S',
          minute: '%H:%M',
          hour: '%l%P',
          day: '%e %b',
          week: '%e %b',
          month: '%b \'%y',
          year: '%Y',
        },
        labels: {
          rotation: 45,
        },
        gridLineWidth: 1,
        minorTicks: true,
      },
      // Two stacked panes sharing the time axis: temperature on top,
      // humidity below, so each reading is easy to read on its own scale.
      yAxis: [
        {
          minorTicks: true,
          title: { text: 'Temperature' },
          height: '45%',
          lineWidth: 1,
          offset: 0,
        },
        {
          minorTicks: true,
          title: { text: 'Humidity' },
          top: '55%',
          height: '45%',
          lineWidth: 1,
          offset: 0,
        },
      ],
    }),
  );
}
